import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import { Config } from '../config'
import { PricingType } from '../types/pricing'

interface UsePriceTestArgs {
  planPrice?: string
  priceExperiment?: string
  hasFreeTrial?: boolean
}

const safeParseJson = (
  pricingJson: string,
  hasFreeTrial: boolean,
  planPrice: string,
  priceExperiment: string
): PricingType => {
  try {
    const data = JSON.parse(pricingJson as string) as PricingType
    const freeTrial = data?.freeTrial ?? hasFreeTrial
    const price = data?.price ?? planPrice
    const experiment = data?.pricingExperiment ?? priceExperiment
    return {
      freeTrial,
      price,
      pricingExperiment: experiment,
    }
  } catch (error) {
    return {
      freeTrial: hasFreeTrial,
      price: planPrice,
      pricingExperiment: priceExperiment,
    }
  }
}

const usePriceTest = ({
  planPrice = '$9.95',
  priceExperiment = 'BASE',
  hasFreeTrial = true,
}: UsePriceTestArgs = {}) => {
  // Only one feature test will be enabled at a time (either user will come from SEM or non SEM)
  const {
    variables: pricingVariables,
    clientReady: pricingClientReady,
    isEnabled: pricingTestEnabled,
  } = useSafeOptimizelyFeature(Config.pricingTestFeatureName)
  const {
    variables: semPricingVariables,
    clientReady: semPricingClientReady,
    isEnabled: semPricingTestEnabled,
  } = useSafeOptimizelyFeature(Config.semPricingTestFeatureName)
  const pricingExperimentReady = pricingClientReady && semPricingClientReady
  const pricingExperimentEnabled = pricingTestEnabled || semPricingTestEnabled
  let freeTrial = hasFreeTrial
  let price = planPrice
  let experiment = priceExperiment
  if (pricingExperimentReady && (pricingTestEnabled || semPricingTestEnabled)) {
    let pricingJson = '{}'
    if (pricingTestEnabled) {
      pricingJson = pricingVariables.pricingJson as string
    } else if (semPricingTestEnabled) {
      pricingJson = semPricingVariables.pricingJson as string
    }
    const data = safeParseJson(pricingJson, hasFreeTrial, planPrice, priceExperiment)
    freeTrial = data.freeTrial
    price = data.price
    experiment = data.pricingExperiment
  }
  return {
    freeTrial,
    price,
    experiment,
    pricingExperimentReady,
    pricingExperimentEnabled,
  }
}

export default usePriceTest
